import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Users, UserMinus, TrendingUp, Download } from "lucide-react";
import {
  getStaffReport,
  getStaffDailyBreakdown,
  getStaffDepartmentReport,
  getStaffTrendAnalysis,
  downloadStaffPdf,
  downloadStaffCsv,
} from "../../services/staff-service";

// ============================================================
// STAT CARD COMPONENT
// ============================================================
const StatCard = ({ title, value, sub, icon, color }) => (
  <div
    style={{
      flex: 1,
      minWidth: "200px",
      backgroundColor: "white",
      borderRadius: "10px",
      padding: "16px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "8px",
      }}
    >
      <h4 style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
        {title}
      </h4>
      <div style={{ color }}>{icon}</div>
    </div>
    <div style={{ fontSize: "24px", fontWeight: "600" }}>{value}</div>
    <div style={{ fontSize: "13px", color: "#6b7280" }}>{sub}</div>
  </div>
);

// ============================================================
// CHART CARD COMPONENT
// ============================================================
const ChartCard = ({ title, subtitle, children, flex }) => (
  <div
    style={{
      flex: flex || 1,
      minWidth: "300px",
      backgroundColor: "white",
      borderRadius: "10px",
      padding: "16px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
      }}
    >
      <h3 style={{ margin: 0, fontWeight: "600", fontSize: "16px" }}>
        {title}
      </h3>
      {subtitle && (
        <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
          {subtitle}
        </p>
      )}
    </div>
    <div>{children}</div>
  </div>
);

// ============================================================
// MAIN DASHBOARD COMPONENT
// ============================================================
export default function StaffAttendanceDashboard() {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [period, setPeriod] = useState("today");
  const [reportData, setReportData] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Fetch main report data
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const data = await getStaffReport({ date: selectedDate });
      setReportData(data.report);
    } catch (error) {
      console.error("Error fetching report:", error);
    }
    setLoading(false);
  };

  // Fetch daily breakdown
  const fetchDailyBreakdown = async () => {
    try {
      const data = await getStaffDailyBreakdown({ end_date: selectedDate });
      setDailyData(data.daily_breakdown || []);
    } catch (error) {
      console.error("Error fetching daily breakdown:", error);
    }
  };

  // Fetch trend analysis
  const fetchTrendAnalysis = async () => {
    try {
      const data = await getStaffTrendAnalysis({ end_date: selectedDate });
      setTrendData(data.weekly_trend || []);
    } catch (error) {
      console.error("Error fetching trend analysis:", error);
    }
  };

  // Fetch department report
  const fetchDepartmentReport = async () => {
    try {
      const data = await getStaffDepartmentReport({ end_date: selectedDate });
      setDepartmentData(data.department_report || []);
    } catch (error) {
      console.error("Error fetching department report:", error);
    }
  };

  // Fetch all data when date changes
  useEffect(() => {
    fetchReportData();
    fetchDailyBreakdown();
    fetchTrendAnalysis();
    fetchDepartmentReport();
  }, [selectedDate]);

  // Download handlers
  const handleDownloadPdf = async () => {
    setDownloadLoading(true);
    try {
      const response = await downloadStaffPdf({ date: selectedDate });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `staff_attendance_${selectedDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF");
    }
    setDownloadLoading(false);
  };

  const handleDownloadCsv = async () => {
    setDownloadLoading(true);
    try {
      const response = await downloadStaffCsv({ date: selectedDate });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `staff_attendance_${selectedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      alert("Failed to download CSV");
    }
    setDownloadLoading(false);
  };

  // Get current period stats and calculate correct absent count
  const rawStats = reportData?.[period] || {
    total_staff: 0,
    present: 0,
    absent: 0,
    attendance_percentage: 0,
  };

  // ✅ FIX: Calculate absent as (total_staff - present) to ensure accuracy
  const currentStats = {
    ...rawStats,
    absent: Math.max(rawStats.total_staff - rawStats.present, 0),
  };

  const pieDatas = [
    { name: "Present", value: currentStats.present, color: "#10b981" },
    { name: "Absent", value: currentStats.absent, color: "#ef4444" },
  ];

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          marginBottom: "20px",
          backgroundColor: "white",
          padding: "16px",
          borderRadius: "10px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div>
            <h1
              style={{
                margin: "0 0 4px 0",
                fontSize: "28px",
                fontWeight: "700",
              }}
            >
              Staff Attendance Dashboard
            </h1>
            <p style={{ margin: 0, color: "#6b7280" }}>
              Comprehensive attendance tracking and analytics
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            />

            <button
              onClick={handleDownloadPdf}
              disabled={downloadLoading}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "none",
                background: "#007bff",
                color: "white",
                cursor: downloadLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              <Download size={16} />
              {downloadLoading ? "Downloading..." : "PDF"}
            </button>

            <button
              onClick={handleDownloadCsv}
              disabled={downloadLoading}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "none",
                background: "#28a745",
                color: "white",
                cursor: downloadLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              <Download size={16} />
              {downloadLoading ? "Downloading..." : "CSV"}
            </button>
          </div>
        </div>
      </div>

      {/* PERIOD SELECTOR */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        {["today", "7D", "30D"].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: period === p ? "#007bff" : "#f3f4f6",
              color: period === p ? "white" : "#374151",
              cursor: "pointer",
              fontWeight: period === p ? "600" : "500",
              fontSize: "14px",
              transition: "all 0.3s ease",
            }}
          >
            {p === "today"
              ? "Today"
              : p === "7D"
              ? "Last 7 Days"
              : "Last 30 Days"}
          </button>
        ))}
      </div>

      {/* STATS CARDS */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <StatCard
          title="ATTENDANCE RATE"
          value={
            <div style={{ color: "#26de81" }}>
              {currentStats.attendance_percentage.toFixed(1)}%
            </div>
          }
          icon={<TrendingUp size={20} />}
          color="#26de81"
          sub={`Out of ${currentStats.total_staff} staff`}
        />

        <StatCard
          title="PRESENT"
          value={
            <div style={{ color: "#10b981" }}>{currentStats.present}</div>
          }
          icon={<Users size={20} />}
          color="#10b981"
          sub={`${((currentStats.present / Math.max(currentStats.total_staff, 1)) * 100).toFixed(1)}% of staff`}
        />

        <StatCard
          title="ABSENT"
          value={
            <div style={{ color: "#ef4444" }}>{currentStats.absent}</div>
          }
          icon={<UserMinus size={20} />}
          color="#ef4444"
          sub={`${((currentStats.absent / Math.max(currentStats.total_staff, 1)) * 100).toFixed(1)}% of staff`}
        />

        <StatCard
          title="TOTAL STAFF"
          value={
            <div style={{ color: "#3b82f6" }}>{currentStats.total_staff}</div>
          }
          icon={<Users size={20} />}
          color="#3b82f6"
          sub="Active staff members"
        />
      </div>

      {/* DAILY BREAKDOWN CHART */}
      <ChartCard
        title="Daily Trend (Last 7 Days)"
        subtitle="Present vs Absent"
        flex={2}
      >
        {dailyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={dailyData}>
              <CartesianGrid vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="day_of_week" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip />
              <Legend />
              <Line
                type="cardinal"
                dataKey="present"
                stroke="#10b981"
                strokeWidth={2}
                dot={true}
                name="Present"
              />
              <Line
                type="cardinal"
                dataKey="absent"
                stroke="#ef4444"
                strokeWidth={2}
                dot={true}
                name="Absent"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: "center", color: "#999", padding: "40px" }}>
            {loading ? "Loading..." : "No data available"}
          </div>
        )}
      </ChartCard>

      {/* ATTENDANCE DISTRIBUTION & DEPARTMENT BREAKDOWN */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <ChartCard
          title="Attendance Distribution"
          subtitle={`${
            period === "today" ? "Today" : period === "7D" ? "Last 7 Days" : "Last 30 Days"
          }`}
          flex={1}
        >
          {pieDatas.some((d) => d.value > 0) || currentStats.total_staff > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={pieDatas}
                  dataKey="value"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {pieDatas.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", color: "#999", padding: "40px" }}>
              No data available
            </div>
          )}
        </ChartCard>

        {/* DEPARTMENT BREAKDOWN */}
        <ChartCard title="Department Breakdown" subtitle="Last 7 days" flex={1}>
          {departmentData.length > 0 ? (
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
              {departmentData.map((dept, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <span style={{ fontWeight: "500", fontSize: "14px" }}>
                    {dept.department}
                  </span>
                  <div style={{ display: "flex", gap: "12px", fontSize: "12px" }}>
                    <span style={{ color: "#10b981", fontWeight: "600" }}>
                      {dept.present}
                    </span>
                    <span style={{ color: "#ef4444", fontWeight: "600" }}>
                      {/* ✅ FIX: Calculate absent for each department */}
                      {Math.max((dept.total || 0) - dept.present, 0)}
                    </span>
                    <span style={{ color: "#3b82f6", fontWeight: "600" }}>
                      {dept.attendance_percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", color: "#999", padding: "40px" }}>
              No data available
            </div>
          )}
        </ChartCard>
      </div>

      {/* WEEKLY TREND ANALYSIS */}
      <ChartCard title="30-Day Trend Analysis" subtitle="Weekly aggregation">
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={trendData}>
              <CartesianGrid vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="week" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="present"
                fill="#10b981"
                radius={[6, 6, 0, 0]}
                name="Present"
              />
              <Bar
                dataKey="absent"
                fill="#ef4444"
                radius={[6, 6, 0, 0]}
                name="Absent"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: "center", color: "#999", padding: "40px" }}>
            {loading ? "Loading..." : "No data available"}
          </div>
        )}
      </ChartCard>
    </div>
  );
}